<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog :value="isDialogOpen" max-width="500px" persistent>
    <div>
      <v-card-title>
        {{ $t('setNewExpirationTime', { expirationTypeName }) }}
      </v-card-title>
      <v-card-text>
        <form :dark="$vuetify.theme.dark">
          <input ref="expirationInput" v-model="newExpirationDate" type="datetime-local" class="datetime" />
          <br />
          <v-label v-if="errorMsg !== ''" class="date-error">{{ errorMsg }}</v-label>
        </form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" :disabled="errorMsg !== ''" text @click="accept">
          <span v-once>{{ $t('accept') }}</span>
        </v-btn>

        <v-btn color="primary" text @click="close">
          <span v-once>{{ $t('close') }}</span>
        </v-btn>
      </v-card-actions>
    </div>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment-timezone';
import businessProcessService from '@/services/businessProcessService';
import campaignService from '@/services/campaignService';
import EcsTimeFormatter from '@/mixins/ecs-time-formatter/EcsTimeFormatter';

const SNACKBAR_MESSAGE_EVENT = 'snackbar-message';

export default {
  name: 'EcsChangeExpirationDialog',

  components: {},

  mixins: [EcsTimeFormatter],

  props: {
    selectedBp: {
      type: Object,
      required: false,
      default: null
    },
    selectedCampaign: {
      type: Object,
      required: false,
      default: null
    },
    now: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      isLoading: false,
      newExpirationDate: this.now.format('yyyy-MM-DDTHH:mm'),
      isDialogOpen: false,
      valid: true,
      timeInterval: null
    };
  },

  computed: {
    ...mapGetters('SETTINGS', ['applicationSettings']),

    ...mapGetters('REFERENCES', ['availableTimeZones']),

    appTimeZone() {
      return this.availableTimeZones.find(tz => tz.value === this.applicationSettings.timeZoneId) || {};
    },

    minutesDiff() {
      if (!this.newExpirationDate) {
        return 0;
      }

      const expirationDate = moment.tz(this.newExpirationDate, this.getIanaTimeZone(this.appTimeZone.id));

      return expirationDate.diff(this.now, 'minutes') + 1;
    },

    errorMsg() {
      if (!this.newExpirationDate) {
        return this.$t('errors.errorNewDateRequired');
      }
      if (this.minutesDiff < 60) {
        return this.$t('errors.errorNewDateHourApart');
      }
      return '';
    },

    expirationTypeName() {
      if (this.selectedBp) {
        return this.$t('businessProcesses');
      } else if (this.selectedCampaign) {
        return this.$t('campaign');
      }
      return '';
    }
  },

  methods: {
    /** This is used to update the local values from the server */
    open() {
      this.isDialogOpen = true;
    },

    accept() {
      if (this.errorMsg !== '') {
        this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
          message: this.errorMsg,
          type: 'error'
        });
        return;
      }

      this.isLoading = true;

      if (this.selectedBp) {
        businessProcessService
          .setTemporaryAssignmentsExpiration(this.selectedBp.id, this.minutesDiff)
          .then(() => {
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('success.updateAssignmentsExpirationTime', {
                businessProcessName: this.selectedBp.name
              }),
              type: 'success'
            });
            this.isDialogOpen = false;
          })
          .catch(() => {
            this.isLoading = false;
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('errors.updateAssignmentsExpirationTime'),
              type: 'error'
            });
          });
      } else if (this.selectedCampaign) {
        campaignService
          .setTemporaryAssignmentsExpiration(this.selectedCampaign.id, this.selectedCampaign.bpid, this.minutesDiff)
          .then(() => {
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('success.updateAssignmentsExpirationTime', {
                campaignName: this.selectedCampaign.name
              }),
              type: 'success'
            });
            this.isDialogOpen = false;
          })
          .catch(() => {
            this.isLoading = false;
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('errors.updateAssignmentsExpirationTime'),
              type: 'error'
            });
          });
      }
    },

    close() {
      this.isDialogOpen = false;
    }
  }
};
</script>

<style scoped>
.group-subheader {
  font-size: 15px;
}

::v-deep .v-dialog {
  background-color: var(--v-surface-base) !important;
  border-color: var(--v-surface-base) !important;
}

.v-dialog > div {
  padding-left: 20px;
}

.datetime {
  font-size: 16px;
}

.datetime::-webkit-calendar-picker-indicator:hover {
  cursor: pointer;
}

[dark] .datetime {
  color: white;
}

[dark] .datetime::-webkit-calendar-picker-indicator {
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 24 24"><path fill="%23bbbbbb" d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>');
}

.date-error {
  top: 10px;
  color: red;
}
</style>
